import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useProjectStore } from '../contexts/projectStoreProvider';
import { useSettings } from '@/contexts/SettingsContext';
import { formatDateByPreference } from '@/utils/dateFormat';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PanelLoading } from '@/components/ui/loading';
import {
  FileText,
  BarChart3,
  Lightbulb,
  FolderOpen,
  Plus,
} from 'lucide-react';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const { projects, organizations, loading, error } = useProjectStore();
  const [selectedOrganization, setSelectedOrganization] = useState<string>('all');

  const stats = {
    projects: projects.length,
    reports: 0,
    insights: 0,
  };

  const recentProjects = projects
    .sort(
      (a, b) =>
        new Date(b.updated_at || b.created_at).getTime() -
        new Date(a.updated_at || a.created_at).getTime()
    )
    .slice(0, 5)
    .map((project) => ({
      id: project.id,
      name: project.name,
      organization: project.organization || 'Unknown',
      type: project.schedules?.[0]?.status || 'Current',
      lastUpdated: project.updated_at || project.created_at,
    }));

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 py-6 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight text-foreground">
                Project Dashboard
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Overview of your projects and activity
              </p>
            </div>
            {organizations.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Organization</span>
                <Select
                  value={selectedOrganization}
                  onValueChange={setSelectedOrganization}
                >
                  <SelectTrigger className="w-[200px] bg-background">
                    <SelectValue placeholder="All organizations" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All organizations</SelectItem>
                    {organizations.map((org) => {
                      const orgId =
                        typeof org === 'object' ? String(org.id) : String(org);
                      const orgName =
                        typeof org === 'object' ? org.name : String(org);
                      return (
                        <SelectItem key={orgId} value={orgId}>
                          {orgName}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <div className="grid gap-6 sm:grid-cols-3 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Projects
              </CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold text-foreground">
                {loading ? '—' : stats.projects}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Reports
              </CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold text-foreground">
                {loading ? '—' : stats.reports}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Insights
              </CardTitle>
              <Lightbulb className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold text-foreground">
                {loading ? '—' : stats.insights}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle>Recent Projects</CardTitle>
              <CardDescription>
                Your most recently updated projects
              </CardDescription>
            </div>
            <Button
              variant="default"
              size="sm"
              onClick={() => navigate('/app/upload')}
            >
              <Plus className="mr-2 h-4 w-4" />
              New Project
            </Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <PanelLoading message="Loading projects…" />
              </div>
            ) : recentProjects.length > 0 ? (
              <ul className="divide-y divide-border">
                {recentProjects.map((project) => (
                  <li key={project.id}>
                    <Link
                      to={`/app/projects/${project.id}`}
                      className="flex flex-col gap-1 py-4 transition-colors hover:bg-muted/50 sm:flex-row sm:items-center sm:justify-between sm:py-3"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-foreground truncate">
                            {project.name}
                          </span>
                          <Badge variant="secondary" size="sm">
                            {project.type}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {project.organization}
                        </p>
                      </div>
                      <p className="text-xs text-muted-foreground shrink-0 sm:text-sm">
                        Updated{' '}
                        {formatDateByPreference(project.lastUpdated, settings.dateFormat, {
                          timeZone: settings.timezone,
                        })}
                      </p>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <FolderOpen className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-1">
                  No projects yet
                </h3>
                <p className="text-sm text-muted-foreground mb-6 max-w-sm">
                  Create a new project by uploading a Primavera P6 XER file.
                </p>
                <Button onClick={() => navigate('/app/upload')}>
                  <Plus className="mr-2 h-4 w-4" />
                  New Project
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Home;
